import React, { useCallback, useId, useState } from 'react'
import { Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Link } from 'gatsby'

import { BLUE_BASE, GREEN, ORANGE, WHITE } from '../../theme/variables/colors'

// Styled components
const Title = styled('span')(({ theme }) => ({
  fontSize: 16,
  lineHeight: '21px',
  letterSpacing: '0.09px',
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: 'block',
}))

const ChipSubLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  position: 'absolute',
  backgroundColor: '#ecf2f5',
  color: `${BLUE_BASE} !important`,
  lineHeight: 'inherit',
  borderRadius: 'inherit',
  width: 'inherit',
  padding: '12px 12px',
  fontSize: 14,
  left: 0,
  right: 0,
  margin: 'auto',
  boxShadow: '1px 1px 3px rgba(0, 0, 0, 0.25)',
  transition: 'all 0.5s cubic-bezier(0.31, -0.96, 0.58, 1.71)',
  zIndex: 1,
}))

const Chip = styled('div')(({ isOpen, theme }) => ({
  backgroundColor: isOpen
    ? theme.palette.primary.dark
    : theme.palette.grey[200],
  lineHeight: 1,
  borderRadius: theme.shape.borderRadius,
  fontSize: 14,
  width: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: isOpen ? WHITE : BLUE_BASE,
  padding: theme.spacing(0.5, 1),
  whiteSpace: 'nowrap',
  minHeight: '32px',
  maxHeight: '32px',
  position: 'relative',
  cursor: 'pointer',
  '& > a:nth-of-type(1)': {
    transform: `scale(${isOpen ? 1 : 0})`,
    opacity: isOpen ? 1 : 0,
    bottom: isOpen ? '-42px' : '0',
  },
  '& > a:nth-of-type(2)': {
    transform: `scale(${isOpen ? 1 : 0})`,
    opacity: isOpen ? 1 : 0,
    bottom: isOpen ? '-84px' : '0',
    transitionDelay: isOpen ? '0.3s' : '0',
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    color: WHITE,
  },
}))

const ColoredArrow = styled('div')(({ productType, isOpen }) => ({
  height: 10,
  width: 10,
  borderRight: `4px solid ${productType === 'ETF' ? ORANGE : GREEN}`,
  borderTop: `4px solid ${productType === 'ETF' ? ORANGE : GREEN}`,
  transform: `rotate(${isOpen ? '135deg' : '45deg'})`,
  transition: 'transform 0.2s ease-in-out',
}))

const ChipsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '5px',
  flexWrap: 'wrap',
}))

// Chip Component
const ChipComponent = React.memo(function ChipComponent({
  title,
  ticker,
  productType,
  productLink,
}) {
  const uniqueId = useId()
  const [isOpen, setIsOpen] = useState(false)

  const handleChipClick = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  return (
    <Tooltip
      title={title}
      placement="top"
      role="button"
      aria-labelledby={`chip-label-${ticker}-${uniqueId}`}
    >
      <Chip isOpen={isOpen} onClick={handleChipClick}>
        <ColoredArrow productType={productType} isOpen={isOpen} />
        <span id={`chip-label-${ticker}-${uniqueId}`}>{ticker}</span>
        <ChipSubLink to={`/insights/product/${ticker.toLowerCase()}`}>
          Insights
        </ChipSubLink>
        <ChipSubLink to={productLink}>Product</ChipSubLink>
      </Chip>
    </Tooltip>
  )
})

// Main Component
export default function RelatedProductsChips({
  products = [],
  withoutLabel = false,
  className,
}) {
  if (products.length === 0) {
    return null
  }

  const sortedProducts = products.sort((a, b) => {
    if (a.product_type < b.product_type) {
      return -1
    }
    if (a.product_type > b.product_type) {
      return 1
    }

    const aTicker = a?.fund_classes?.[0]?.ticker
    const bTicker = b?.fund_classes?.[0]?.ticker

    return aTicker < bTicker ? -1 : aTicker > bTicker ? 1 : 0
  })

  return (
    <div className={className}>
      {!withoutLabel && <Title>Related Products</Title>}
      <ChipsContainer>
        {sortedProducts.map((product) => {
          const { title, product_type, url, fund_classes } = product
          const ticker = fund_classes?.[0]?.ticker ?? ''
          return (
            <ChipComponent
              key={ticker} // Use a unique key
              title={title}
              ticker={ticker}
              productType={product_type}
              productLink={url ?? ''}
            />
          )
        })}
      </ChipsContainer>
    </div>
  )
}
